import 'vite/modulepreload-polyfill'
import 'polyfill-object.fromentries'
import './index.css'

import CssBaseline from '@mui/material/CssBaseline'
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import * as Sentry from '@sentry/browser'
import * as React from 'react'
import ReactDOM from 'react-dom'
import { QueryClient, QueryClientProvider } from 'react-query'
import { Provider } from 'react-redux'
import { BrowserRouter as Router } from 'react-router-dom'
import { CompatRouter } from 'react-router-dom-v5-compat'
import { redirectFromLegacyRoute } from 'shared/helpers/redirectFromLegacyRoute'
import { muiTheme } from 'shared/theme'
import { VibeKanbanWebCompanion } from 'vibe-kanban-web-companion'

import { PersistGate } from 'redux-persist/integration/react'
import { App } from './App'
import { AppContainer } from './AppContainer'
import { GlobalStyles } from './components/GlobalStyles/GlobalStyles'
import { detectEnvironment, getBranchKey, GTM_CONTAINER, isProduction, SENTRY_DSN } from './config/config'
import { handleUnauthorizedRequest, setCsrfHeader, setXHeaders } from './helpers/authHelper'
import { useIsPath } from './hooks/useIsPath'
import { MainErrorBoundary } from './pages/ErrorPage/MainErrorBoundary'
import { persistor } from './redux/store/persistor'
import { store } from './redux/store/store'
import { marketingRoutesPaths } from './Routes'
import { loadBranch } from './util/branch'
import { loadTagManager } from './util/googleTagManager'
;(window as any).heap = (window as any).heap || []
redirectFromLegacyRoute()
loadTagManager(GTM_CONTAINER)
loadBranch(getBranchKey())

if (isProduction) {
  Sentry.init({
    dsn: SENTRY_DSN,
    release: process.env.REACT_APP_RELEASE_VERSION,
    environment: detectEnvironment().toLowerCase(),
    allowUrls: [
      'app.cloudtrucks.com',
      'static.cloudtrucks.com',
      'app.cloudtrucks-staging.com',
      'static.cloudtrucks-staging.com',
    ],
    normalizeDepth: 7,

    tracesSampleRate: 0.1,
    profilesSampleRate: 1.0,
    replaysSessionSampleRate: 0,
    // @sentry/replay @LogRocket ffr
    // https://docs.sentry.io/platforms/javascript/session-replay/#verify
    replaysOnErrorSampleRate: 0.1,
    integrations: [new Sentry.Replay()],
  })

  Sentry.configureScope((scope) => {
    scope.setTag('React.version', React.version)
  })
}

// set up interceptor to set csrf token in the header for every request
setCsrfHeader()
setXHeaders()
// set interceptor to redirect user to login if authorized
handleUnauthorizedRequest()

const queryClient = new QueryClient()

ReactDOM.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <MuiThemeProvider theme={muiTheme}>
        <QueryClientProvider client={queryClient}>
          <Router>
            <CompatRouter>
              <CssBaseline />
              <GlobalStyles />
              <CustomApp />
              <VibeKanbanWebCompanion />
            </CompatRouter>
          </Router>
        </QueryClientProvider>
      </MuiThemeProvider>
    </PersistGate>
  </Provider>,
  document.getElementById('root')
)

function CustomApp() {
  const isMarketingPath = useIsPath(marketingRoutesPaths)
  const AppComponent = isMarketingPath ? AppContainer : App

  return (
    <MainErrorBoundary>
      <AppComponent />
    </MainErrorBoundary>
  )
}
